// =======================================================
// CircuitPulse — TechWolf-style circuit grid with data pulses
// =======================================================
//
// Port of the TouchDesigner "AI Day 2026" pixel shader: a randomised
// circuit-board grid layered with hex lattice, FBM warp, scanning data
// pulses, glitch lines and a vignette. Single fullscreen shader.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float u_time;
  uniform vec2  uResolution;
  uniform vec3  uAccent;
  uniform vec3  uAccentDim;
  uniform vec3  uBg;
  uniform float uCircuitDensity;
  uniform float uPulseStrength;
  uniform float uStreamStrength;
  uniform float uGlitchStrength;
  uniform float uScanStrength;
  uniform float uSpeed;

  #define PI  3.14159265358979
  #define TAU 6.28318530717959

  float hashC(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float noiseC(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hashC(i), hashC(i + vec2(1.0, 0.0)), f.x),
      mix(hashC(i + vec2(0.0, 1.0)), hashC(i + vec2(1.0, 1.0)), f.x),
      f.y);
  }

  float fbmC(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) { v += a * noiseC(p); p *= 2.1; a *= 0.5; }
    return v;
  }

  float lineSDF(vec2 p, vec2 a, vec2 b, float w) {
    vec2 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return smoothstep(w, 0.0, length(pa - ba * h));
  }

  float circuit(vec2 uv, float density) {
    float g = 0.0;
    vec2 cell  = floor(uv * density);
    vec2 local = fract(uv * density) - 0.5;
    float h1 = hashC(cell);
    float h2 = hashC(cell + vec2(13.0, 7.0));
    if (h1 > 0.5) {
      g += lineSDF(local, vec2(-0.5, 0.0), vec2(0.5, 0.0), 0.015);
      if (h2 > 0.7) g += lineSDF(local, vec2(0.0), vec2(0.0,  0.5), 0.015);
      if (h2 < 0.3) g += lineSDF(local, vec2(0.0), vec2(0.0, -0.5), 0.015);
    } else {
      g += lineSDF(local, vec2(0.0, -0.5), vec2(0.0, 0.5), 0.015);
      if (h2 > 0.6) g += lineSDF(local, vec2(0.0), vec2(0.5, 0.0), 0.015);
    }
    if (hashC(cell + vec2(3.0, 9.0)) > 0.75)
      g += smoothstep(0.06, 0.0, length(local)) * 0.8;
    return g;
  }

  float hexGrid(vec2 uv) {
    vec2 r = vec2(1.0, 1.732), h = r * 0.5;
    vec2 a = mod(uv, r) - h;
    vec2 b = mod(uv + h, r) - h;
    return min(dot(a, a), dot(b, b));
  }

  float dataPulses(vec2 uv, float t) {
    float result = 0.0;
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float spd = mod(t * 0.6 + fi * 0.25 + hashC(vec2(fi, 0.0)) * 0.1, 1.0);
      result += smoothstep(0.015, 0.0, abs(uv.x - spd))
              * smoothstep(0.0, 1.0, sin(uv.y * PI)) * 0.7;
    }
    return result;
  }

  float dataStreams(vec2 uv, float t) {
    float result = 0.0;
    for (int j = 0; j < 6; j++) {
      float fj = float(j);
      float yLine = (fj + 0.5) / 6.0;
      float ds = smoothstep(0.004, 0.0, abs(uv.y - yLine));
      float mask = step(0.5, fract(uv.x * 40.0 - t * (1.0 + fj * 0.3)));
      result += ds * mask * 0.4;
    }
    return result;
  }

  float glitchFX(vec2 uv, float t) {
    float gi = floor(t * 8.0);
    if (hashC(vec2(gi, gi * 0.7)) > 0.8) {
      float gline = hashC(vec2(gi * 1.3, 0.0));
      return smoothstep(0.005, 0.0, abs(uv.y - gline))
           * hashC(vec2(gi, uv.x * 80.0)) * 0.5;
    }
    return 0.0;
  }

  void main() {
    vec2 uv = vUv;
    float tRaw = u_time * uSpeed;
    float t = mod(tRaw * 0.15, TAU);

    float c1 = circuit(uv, uCircuitDensity);
    float c2 = circuit(uv + vec2(mod(t * 0.05, 1.0) + 0.5, 0.25), uCircuitDensity);

    float hex     = hexGrid((uv + vec2(mod(t * 0.02, 1.0), 0.0)) * vec2(16.0, 9.0));
    float hexLine = smoothstep(0.22, 0.18, hex) * 0.12;

    float n  = fbmC(uv * 3.0 + vec2(mod(t * 0.3, 10.0), mod(t * 0.1, 10.0)));
    float n2 = fbmC(uv * 6.0 - vec2(mod(t * 0.2, 10.0), mod(t * 0.15, 10.0)) + n);

    float scan = smoothstep(0.995, 1.0, abs(sin(uv.y * 240.0 + tRaw * 2.0))) * 0.08;
    float pu   = dataPulses(uv, t) * uPulseStrength;
    float ds   = dataStreams(uv, t) * uStreamStrength;
    float gx   = glitchFX(uv, tRaw) * uGlitchStrength;
    float vign = 1.0 - smoothstep(0.3, 1.0, length((uv - 0.5) * vec2(2.0, 0.5)));

    vec3 col = uBg;
    col += uAccentDim * hexLine;
    col += uAccent * c1 * 0.45;
    col += uAccent * c2 * 0.20;
    col += uAccent * pu;
    col += uAccent * ds;
    col += uAccent * gx;
    col += uAccent * n2 * 0.04;
    col -= vec3(0.03) * scan * uScanStrength;
    col *= vign;

    float bloom = c1 + pu * 0.5;
    col += uAccent * bloom * bloom * 0.15;
    col = pow(clamp(col, 0.0, 1.0), vec3(0.9));

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class CircuitPulse extends Generator {
  constructor() {
    super({
      name: 'Circuit Pulse',
      category: 'intense',
      schema: {
        speed:    { default: 1.0,  min: 0,   max: 4,   step: 0.01, label: 'Speed' },
        density:  { default: 24,   min: 6,   max: 64,  step: 1,    label: 'Circuit density' },
        pulse:    { default: 1.0,  min: 0,   max: 2,   step: 0.01, label: 'Pulse strength' },
        stream:   { default: 1.0,  min: 0,   max: 2,   step: 0.01, label: 'Stream strength' },
        glitch:   { default: 1.0,  min: 0,   max: 2,   step: 0.01, label: 'Glitch strength' },
        scan:     { default: 1.0,  min: 0,   max: 2,   step: 0.01, label: 'Scanline strength' },
        accent:   { default: '#3dffc7', type: 'color', label: 'Accent' },
        accentDim:{ default: '#0d382c', type: 'color', label: 'Accent (dim)' },
        bg:       { default: '#08090c', type: 'color', label: 'Background' },
      }
    });
  }

  onEnter(scene, engine) {
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        u_time:          { value: 0 },
        uResolution:     { value: new THREE.Vector2(engine.width, engine.height) },
        uAccent:         { value: new THREE.Color(this.v('accent')) },
        uAccentDim:      { value: new THREE.Color(this.v('accentDim')) },
        uBg:             { value: new THREE.Color(this.v('bg')) },
        uCircuitDensity: { value: this.v('density') },
        uPulseStrength:  { value: this.v('pulse') },
        uStreamStrength: { value: this.v('stream') },
        uGlitchStrength: { value: this.v('glitch') },
        uScanStrength:   { value: this.v('scan') },
        uSpeed:          { value: this.v('speed') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent')    u.uAccent.value.set(val);
    if (key === 'accentDim') u.uAccentDim.value.set(val);
    if (key === 'bg')        u.uBg.value.set(val);
    if (key === 'density')   u.uCircuitDensity.value = val;
    if (key === 'pulse')     u.uPulseStrength.value = val;
    if (key === 'stream')    u.uStreamStrength.value = val;
    if (key === 'glitch')    u.uGlitchStrength.value = val;
    if (key === 'scan')      u.uScanStrength.value = val;
    if (key === 'speed')     u.uSpeed.value = val;
  }

  update(dt, time, engine) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.u_time.value = time;
  }
}
